import { saveMail } from '../../../services/api/dataServiceOutlookQuickSave';
import { ISaveInfo, IContactInfo, DateOnly, unrestrictedAccess } from '../model/QuickSaveModel';
import { IDocumentDetails } from '../../../@types/models';
import type {
    InsertableArchiveResult,
    EmailDocumentCategoryResult,
    CaseResult,
    RecordTypeResult,
    CarbonCopyContactResult,
    ValidateArchiveAndCaseResult
} from '../../../services/api/dataServiceOutlookQuickSave';

export interface SubmitCallbacks {
    onStart: () => void;
    onSuccess: (documentDetails?: IDocumentDetails | null) => void;
    onError: (error: string) => void;
    onImportArchive: (documentDetails: IDocumentDetails) => void;
}

export interface SubmitData {
    title: string;
    selectedArchive: InsertableArchiveResult;
    selectedCategory: EmailDocumentCategoryResult;
    selectedCase: CaseResult;
    selectedRecordType: RecordTypeResult | null;
    selectedCopyTo: CarbonCopyContactResult[];
    caseValidation: ValidateArchiveAndCaseResult | null;
}

export const submitQuickSave = (data: SubmitData, callbacks: SubmitCallbacks): void => {
    const { onStart, onSuccess, onError, onImportArchive } = callbacks;
    const {
        title,
        selectedArchive,
        selectedCategory,
        selectedCase,
        selectedRecordType,
        selectedCopyTo
    } = data;

    onStart();

    try {
        // Convert CarbonCopyContactResult to IContactInfo format
        const ccContacts: IContactInfo[] = selectedCopyTo.map(contact => ({
            Recno: contact.Recno,
            Name: contact.Name,
            IsInternal: contact.IsInternal,
            Domain: contact.Domain,
            EmployerRecno: contact.EmployerRecno || 0,
            EmployerName: contact.EmployerName,
            Address: contact.Address,
            Zip: contact.Zip
        }));

        // Create access properties - use default since validation returns different interface
        const accessProperties = unrestrictedAccess;

        // Prepare ISaveInfo object according to interface
        const saveInfo: ISaveInfo = {
            documentArchive: selectedArchive.Recno.toString(),
            documentCategory: selectedCategory.Recno.toString(),
            caseReno: selectedCase.Recno.toString(),
            documentTitle: title,
            ccContact: ccContacts.length > 0 ? ccContacts : null,
            recordType: selectedRecordType ? selectedRecordType.Recno.toString() : null,
            accessProps: accessProperties,
            documentDate: DateOnly.Today()
        };

        // Call saveMail function
        void saveMail(
            saveInfo,
            (response: IDocumentDetails | null) => {
                // Check if this is an import archive response
                if (response?.IsInImportArchive) {
                    onImportArchive(response);
                } else {
                    // Success callback with document details
                    onSuccess(response);
                }
            },
            (error) => {
                // Error callback
                onError(error);
            }
        );

    } catch (err) {
        onError(err instanceof Error ? err.message : 'Error preparing save data');
    }
};
